const CONJUNCTIONS = [
  '그러나', '그렇지만', '하지만', '그런데', '한데',
  '그리고', '또한', '게다가', '더욱이', '뿐만 아니라', '더불어',
  '따라서', '그래서', '그러므로', '이에', '이로 인해',
  '또는', '혹은',
  '한편', '다른 한편으로', '반면에', '이와 반대로', '이와 달리',
  '즉', '다시 말해', '다시 말하면',
  '이처럼', '이렇게',
  '이후', '이전',
  '동시에',
  '오히려',
  '특히', '특별히',
  '예를 들어', '예컨대',
  '먼저', '둘째', '셋째', '첫째', '다음으로', '마지막으로',
  '물론', '아마도', '실제로', '사실', '결국',
];

function startsWithConjunction(sentence: string): boolean {
  const trimmed = sentence.trim().replace(/^["'「『]/, '').trim();
  return CONJUNCTIONS.some(c => trimmed.startsWith(c));
}

function isInUrl(text: string, pos: number): boolean {
  const before = text.slice(0, pos);
  const lastSpace = Math.max(before.lastIndexOf(' '), before.lastIndexOf('\n'));
  const wordStart = lastSpace + 1;
  const word = before.slice(wordStart);
  return /^https?:\/\//.test(word) || /^www\./.test(word);
}

function splitSentences(text: string): string[] {
  // Precompute whether each position is inside quotes or parentheses
  const inside: boolean[] = new Array(text.length).fill(false);
  let dblQuote = 0;
  let sglQuote = 0;
  let paren = 0;
  let bracket = 0;
  let brace = 0;
  let guillemet1 = 0; // 「」
  let guillemet2 = 0; // 『』

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const prev = text[i - 1];
    const next = text[i + 1];

    if (ch === '"') {
      dblQuote++;
    } else if (ch === "'" && !(prev && /[a-zA-Z]/.test(prev) && next && /[a-zA-Z]/.test(next))) {
      sglQuote++;
    } else if (ch === '(') {
      paren++;
    } else if (ch === ')') {
      paren = Math.max(0, paren - 1);
    } else if (ch === '[') {
      bracket++;
    } else if (ch === ']') {
      bracket = Math.max(0, bracket - 1);
    } else if (ch === '{') {
      brace++;
    } else if (ch === '}') {
      brace = Math.max(0, brace - 1);
    } else if (ch === '「') {
      guillemet1++;
    } else if (ch === '」') {
      guillemet1 = Math.max(0, guillemet1 - 1);
    } else if (ch === '『') {
      guillemet2++;
    } else if (ch === '』') {
      guillemet2 = Math.max(0, guillemet2 - 1);
    }

    inside[i] = (dblQuote % 2 === 1) || (sglQuote % 2 === 1) ||
                paren > 0 || bracket > 0 || brace > 0 ||
                guillemet1 > 0 || guillemet2 > 0;
  }

  const sentences: string[] = [];
  let current = '';

  for (let i = 0; i < text.length; i++) {
    current += text[i];

    if (text[i] !== '.' && text[i] !== '?' && text[i] !== '!') continue;

    // Skip repeated punctuation like ... or !!!
    if (i + 1 < text.length && (text[i + 1] === '.' || text[i + 1] === '?' || text[i + 1] === '!')) continue;

    if (isInUrl(text, i)) continue;

    if (inside[i]) continue;

    const rest = text.slice(i + 1);
    const match = rest.match(/^(\s*)(.)/);
    const prevChar = text[i - 1];

    if (!prevChar || !/[가-힣a-zA-Z]/.test(prevChar)) continue;

    const isEndOfText = !match;
    const nextChar = match?.[2];
    const leadingSpaces = match ? match[1].length : 0;

    const isNewSentence = isEndOfText ||
      /[가-힣]/.test(nextChar) ||
      /[A-Z]/.test(nextChar) ||
      nextChar === '"' ||
      nextChar === "'" ||
      nextChar === '「' ||
      nextChar === '『';

    if (!isNewSentence) continue;

    // Abbreviation: English letter before + lowercase English after
    if (/[a-zA-Z]/.test(prevChar) && /[a-z]/.test(nextChar)) continue;

    sentences.push(current.trim());
    current = '';
    i += leadingSpaces;
  }

  if (current.trim()) sentences.push(current.trim());
  return sentences.filter(s => s.length > 0);
}

function calculateTargetSizes(total: number): number[] {
  const targets: number[] = [];
  let remaining = total;
  while (remaining > 0) {
    if (remaining === 4 || remaining === 2) {
      targets.push(2);
      remaining -= 2;
    } else {
      targets.push(3);
      remaining -= 3;
    }
  }
  return targets;
}

function splitSentencesIntoParagraphs(sentences: string[]): string[][] {
  const n = sentences.length;
  if (n <= 3) return [sentences];

  const targets = calculateTargetSizes(n);
  const result: string[][] = [];
  let idx = 0;

  for (let i = 0; i < targets.length; i++) {
    const targetSize = targets[i];
    const remainingSentences = n - idx;
    const remainingGroups = targets.length - i;

    if (i === targets.length - 1) {
      result.push(sentences.slice(idx));
      break;
    }

    const minSize = 2;
    const maxSize = Math.min(
      remainingSentences - minSize * (remainingGroups - 1),
      targetSize + 2
    );

    const candidates: number[] = [];
    for (let d = 0; d <= 2; d++) {
      if (targetSize + d >= minSize && targetSize + d <= maxSize && !candidates.includes(targetSize + d)) {
        candidates.push(targetSize + d);
      }
      if (d > 0 && targetSize - d >= minSize && targetSize - d <= maxSize && !candidates.includes(targetSize - d)) {
        candidates.push(targetSize - d);
      }
    }

    let bestSize = targetSize;
    for (const size of candidates) {
      const splitPos = idx + size;
      if (splitPos < n && startsWithConjunction(sentences[splitPos])) {
        bestSize = size;
        break;
      }
    }

    result.push(sentences.slice(idx, idx + bestSize));
    idx += bestSize;
  }

  return result;
}

function isPlainParagraph(block: string): boolean {
  const trimmed = block.trim();
  if (!trimmed) return false;
  if (/^#{1,6}\s/.test(trimmed)) return false;
  if (/^```/.test(trimmed)) return false;
  if (/^(\s*[-*]\s|\s*\d+\.\s)/.test(trimmed)) return false;
  if (/^\s*>/.test(trimmed)) return false;
  if (/^(---|\*\*\*|___)\s*$/.test(trimmed)) return false;
  if (/^<[a-zA-Z]/.test(trimmed)) return false;
  if (/^!\[/.test(trimmed)) return false;
  if (/^\|/.test(trimmed)) return false;
  return true;
}

export function splitLongParagraphs(markdown: string): string {
  const normalized = markdown.replace(/\r\n/g, '\n');
  const blocks = normalized.split('\n\n');

  const processed = blocks.map(block => {
    if (!isPlainParagraph(block)) return block;

    const sentences = splitSentences(block);
    if (sentences.length <= 3) return block;

    const paragraphs = splitSentencesIntoParagraphs(sentences);
    return paragraphs.map(p => p.join(' ')).join('\n\n');
  });

  return processed.join('\n\n');
}
